import React from 'react'
import { Box, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import CheckIcon from '@mui/icons-material/Check'

export interface SelectCardProps {
  selected?: boolean
  disabled?: boolean
  /** Image URL for visual assets (scene, frame, material) */
  imageUrl?: string
  /** Omit (or pass '') to render an image-only tile with no caption row — e.g. a plain photo grid. */
  title?: string
  subtitle?: string
  aspectRatio?: string
  onClick?: () => void
  className?: string
}

const Root = styled('button', {
  shouldForwardProp: (p) => p !== '$selected' && p !== '$disabled',
})<{ $selected?: boolean; $disabled?: boolean }>(
  ({ $selected, $disabled }) => ({
    all: 'unset',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    cursor: $disabled ? 'not-allowed' : 'pointer',
    opacity: $disabled ? 0.45 : 1,
    borderRadius: 20,
    overflow: 'hidden',
    backgroundColor: 'var(--ds-canvas)',
    boxShadow: $selected ? 'var(--ds-shadow-soft)' : 'none',
    outline: $selected ? '2px solid var(--ds-ink)' : '1px solid var(--ds-border)',
    outlineOffset: 0,
    transition: [
      'transform var(--ds-duration-normal) var(--ds-ease)',
      'outline-color var(--ds-duration-fast) var(--ds-ease)',
      'box-shadow var(--ds-duration-normal) var(--ds-ease)',
    ].join(', '),
    '&:hover:not([disabled])': {
      transform: 'translateY(-2px)',
      boxShadow: 'var(--ds-shadow-soft)',
    },
    '&:active:not([disabled])': {
      transform: 'translateY(0)',
    },
    '&:focus-visible': {
      outline: '2px solid var(--ds-ink)',
      outlineOffset: 2,
    },
  }),
)

const Media = styled(Box, {
  shouldForwardProp: (p) => p !== '$ratio',
})<{ $ratio: string }>(({ $ratio }) => ({
  width: '100%',
  aspectRatio: $ratio,
  backgroundColor: 'var(--ds-hover)',
  overflow: 'hidden',
  position: 'relative',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
}))

const CheckBadge = styled(Box)({
  position: 'absolute',
  top: 10,
  right: 10,
  width: 24,
  height: 24,
  borderRadius: 'var(--ds-radius-pill)',
  background: 'var(--ds-ink)',
  color: 'var(--ds-inverse-ink)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 2px 8px rgba(0,0,0,0.22)',
})

const EmptyMedia = styled(Box)({
  width: '100%',
  height: '100%',
  background:
    'repeating-linear-gradient(135deg, var(--ds-hover) 0 6px, transparent 6px 12px)',
})

const Meta = styled(Box)({
  padding: '12px 14px 14px',
  borderTop: '1px solid var(--ds-border)',
})

/** Selectable asset tile — scenes, frames, materials */
export function SelectCard({
  selected,
  disabled,
  imageUrl,
  title,
  subtitle,
  aspectRatio = '16 / 10',
  onClick,
  className,
}: SelectCardProps) {
  return (
    <Root
      type='button'
      className={className}
      $selected={selected}
      $disabled={disabled}
      disabled={disabled}
      aria-pressed={selected}
      onClick={onClick}
    >
      <Media $ratio={aspectRatio}>
        {imageUrl ? <img src={imageUrl} alt='' loading='lazy' /> : <EmptyMedia />}
        {selected && (
          <CheckBadge aria-hidden>
            <CheckIcon sx={{ fontSize: 14 }} />
          </CheckBadge>
        )}
      </Media>
      {title && (
        <Meta>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: selected ? 600 : 500,
              letterSpacing: '-0.01em',
              color: 'var(--ds-ink)',
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: 12, color: 'var(--ds-ink-muted)', mt: 0.25, lineHeight: 1.35 }}>
              {subtitle}
            </Typography>
          )}
        </Meta>
      )}
    </Root>
  )
}
